import type { BaseSubscriptionOptions } from '@apollo/client';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { Subscription } from 'components/Subscription';
import {
  AUCTION_SUBSCRIPTION_ROUTES,
  OFFERS_SUBSCRIPTION_ROUTES,
  PRIMARY_OFFERS_SUBSCRIPTION_ROUTES,
  primaryOfferSubscription,
  tokenAuctionSubscription,
  tokenOfferSubscription,
} from 'lib/subscriptions';

const matchRoutes = (routes: string[], pathname: string) =>
  routes.some(
    route => matchAnySportPath(route, pathname) || matchPath(route, pathname)
  );

const subscriptionsOptions = (sport: Sport): BaseSubscriptionOptions => ({
  variables: { sports: [sport] },
});

type Props = {
  sport: Sport;
};

export const MarketSubscriptions = ({ sport }: Props) => {
  const location = useLocation();
  const subscribeToAuctions = useMemo(
    () => matchRoutes(AUCTION_SUBSCRIPTION_ROUTES, location.pathname),
    [location.pathname]
  );

  const subscribeToOffers = useMemo(
    () => matchRoutes(OFFERS_SUBSCRIPTION_ROUTES, location.pathname),
    [location.pathname]
  );

  const subscribeToPrimaryOffer = useMemo(
    () => matchRoutes(PRIMARY_OFFERS_SUBSCRIPTION_ROUTES, location.pathname),
    [location.pathname]
  );

  return (
    <>
      {!!subscribeToAuctions && (
        <Subscription
          gql={tokenAuctionSubscription}
          options={subscriptionsOptions(sport)}
        />
      )}
      {!!subscribeToOffers && (
        <Subscription
          gql={tokenOfferSubscription}
          options={subscriptionsOptions(sport)}
        />
      )}
      {!!subscribeToPrimaryOffer && (
        <Subscription
          gql={primaryOfferSubscription}
          options={subscriptionsOptions(sport)}
        />
      )}
    </>
  );
};
